import React, { Fragment, useEffect, useState } from 'react'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import NavLink from 'components/navLink'
import Button from 'components/button'
import LinkAnimate from 'utils/hooks/linkAnimate'
import DetectClickOutside from 'utils/hooks/detectClickOutside'
import { iconRightSolid } from 'utils/icons'
import { COMMON } from 'utils/intl/common.js'
import { checkScrollDirectionIsUp } from 'utils'

function Header(props) {
  const { data, headerIsDark, headerNoSticky, headerIsTransparent } = props
  const breakpoints = useBreakpoint()

  const [isOpen, setIsOpen] = useState(false)
  const [isUp, setIsUp] = useState(false)
  const [isDown, setIsDown] = useState(false)
  const [isSticky, setIsSticky] = useState(false)

  useEffect(() => {
    const header = document.querySelector('.header')
    const headerHeight = header.getBoundingClientRect().height
    const handleScroll = (event) => {
      const scrollY = window.scrollY
      const menuSticky = scrollY > headerHeight / 3
      const scrollDirectionUp = checkScrollDirectionIsUp(event)

      if (menuSticky) {
        setIsSticky(true)
      }

      if (scrollDirectionUp) {
        setIsUp(true)
        setIsDown(false)
      } else {
        setIsDown(true)
        setIsUp(false)
      }

      if (scrollY === 0) {
        setIsUp(false)
        setIsDown(false)
        setIsSticky(false)
      }
    }

    // Remove useless property when resize window
    const handleResize = () => {
      const body = document.querySelector('body')
      const pageOpacity = body.querySelector('.page-opacity')
      const headerSubmenu = body.querySelector('.header__submenu')
      const headerSubmenuContainer = headerSubmenu.querySelector('.header__submenu__container')
      setIsOpen(false)
      headerSubmenu.removeAttribute('style')
      headerSubmenuContainer.removeAttribute('style')
      pageOpacity.classList.remove('is-active')
      body.classList.remove('is-hidden')
    }

    if (!headerNoSticky) {
      window.addEventListener('wheel', (e) => handleScroll(e))
      window.addEventListener('touchstart', (e) => handleScroll(e))
    }
    window.addEventListener('resize', handleResize)

    return () => {
      const pageOpacity = document.querySelector('.page-opacity')
      pageOpacity.classList.remove('is-active')
      const body = document.querySelector('body')
      body.classList.remove('is-hidden')
      window.removeEventListener('wheel', (e) => handleScroll(e))
      window.removeEventListener('touchstart', (e) => handleScroll(e))
      window.removeEventListener('resize', handleResize)
    }
  }, [headerNoSticky])

  // On click previous for mobile only
  const onClickPreviousBtn = () => {
    const headerSubmenu = document.querySelector('.header__submenu')
    headerSubmenu.style.display = 0
    headerSubmenu.style.pointerEvents = 'none'
    headerSubmenu.style.zIndex = -10

    const headerSections = headerSubmenu.querySelectorAll('section')
    for (let i = 0; i < headerSections.length; i++) {
      const section = headerSections[i]
      section.classList.remove('is-selected')
    }
  }

  // Toggle display mobile menu
  const toggleMobileMenu = (isOpen) => {
    const body = document.querySelector('body')
    setIsOpen(!isOpen)

    if (!isOpen) {
      onClickPreviousBtn()
      body.classList.add('is-hidden')
    } else {
      body.classList.remove('is-hidden')
    }
  }

  // Display menu section mobile and desktop version
  const setDisplayMenuSection = (mouseEnter, event, index) => {
    const pageOpacity = document.querySelector('.page-opacity')

    if (mouseEnter) {
      setIsOpen(mouseEnter)

      const currentLink = event.currentTarget
      const currentLinkPosition = currentLink.getBoundingClientRect()

      const header = document.querySelector('.header')
      const headerSubmenu = header.querySelector('.header__submenu')
      const headerSubmenuContainer = headerSubmenu.querySelector('.header__submenu__container')
      const headerArrow = headerSubmenu.querySelector('.header__submenu__arrow')
      const headerSections = headerSubmenu.querySelectorAll('section')

      // Reset section selected
      for (let i = 0; i < headerSections.length; i++) {
        const section = headerSections[i]
        section.classList.remove('is-selected')
      }

      // Get current section when hover or click
      const currentSection = Array.from(headerSections).filter(
        (section) => section.getAttribute('data-index') === `${index}` && section
      )[0]

      if (currentSection.length === 0) return
      currentSection.classList.add('is-selected')

      // Mobile property only
      if (!breakpoints.menu) {
        headerSubmenu.style.opacity = 1
        headerSubmenu.style.zIndex = 4
        setTimeout(() => (headerSubmenu.style.pointerEvents = 'initial'), 500)
        return
      }

      // Animation for desktop submenu
      const padding = index === 1 ? 16 : 48
      pageOpacity.classList.add('is-active')
      headerSubmenuContainer.style.width = `${currentSection.getBoundingClientRect().width + padding}px`
      headerSubmenuContainer.style.height = `${currentSection.getBoundingClientRect().height + 48}px`
      headerSubmenu.style.transform = `translateX(${currentLinkPosition.x - currentLinkPosition.width / 2}px)`
      headerArrow.style.transform = `translateX(${currentLinkPosition.width / 2 - 8}px)`
    } else {
      // Remove active menu desktop and mobile
      setIsOpen(mouseEnter)
      pageOpacity.classList.remove('is-active')
    }
  }

  return (
    <DetectClickOutside callback={() => setIsOpen(false)} event="mouseover">
      <header
        className={`header ${isOpen ? 'is-open' : ''} ${headerIsDark ? 'is-dark' : ''} ${isUp ? 'is-up' : ''} ${
          isDown ? 'is-down' : ''
        } ${isSticky ? 'is-sticky' : ''} ${!headerIsTransparent && headerNoSticky ? 'is-absolute' : ''} ${
          headerIsTransparent ? 'is-transparent' : ''
        }`}
      >
        <div className="container">
          <LinkAnimate className="header__logo" to="/">
            <img loading="lazy" src="../../../images/logo-white.svg" alt="Qovery white logo" />
          </LinkAnimate>
          <div className="header__menu">
            <p className="header__title h2 ft-700 mt-9 mb-0 hide-menu">Menu</p>
            <nav role="presentation" onMouseLeave={() => breakpoints.menu && setDisplayMenuSection(false)}>
              {data.links.map((links, index) => (
                <Fragment key={index}>
                  {!links.block.document.type ? (
                    <div
                      role="button"
                      tabIndex="0"
                      className="header__link"
                      onMouseEnter={(e) => breakpoints.menu && setDisplayMenuSection(true, e, index)}
                      onClick={(e) => !breakpoints.menu && setDisplayMenuSection(true, e, index)}
                      onKeyDown={(e) => !breakpoints.menu && setDisplayMenuSection(true, e, index)}
                    >
                      <button>
                        {links.block.document.data.title.text}
                        <span className="hide-tablet">{iconRightSolid}</span>
                      </button>
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex="0"
                      className="header__link"
                      onMouseEnter={() => breakpoints.menu && setDisplayMenuSection(false)}
                      onClick={() => !breakpoints.menu && setDisplayMenuSection(false)}
                      onKeyDown={() => !breakpoints.menu && setDisplayMenuSection(false)}
                    >
                      {links.block.document.data.link.url ? (
                        <a href={links.block.document.data.link.url} rel="noopener noreferrer" target="_blank">
                          {links.block.document.data.title.text}
                        </a>
                      ) : (
                        <LinkAnimate to={`${links.block.document.data.slug.text}`}>
                          {links.block.document.data.title.text}
                        </LinkAnimate>
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
              <div role="button" tabIndex="0" className="header__link hide-tablet">
                <a href="https://www.qovery.com/request-access?formId=yFDkReLr" rel="noopener noreferrer">
                  {COMMON.GET_A_DEMO}
                </a>
              </div>
              <div role="button" tabIndex="0" className="header__link hide-tablet">
                <a href="https://start.qovery.com" rel="noopener noreferrer">
                  {COMMON.SIGN_UP}
                </a>
              </div>
              <div className="header__submenu">
                <div className="header__submenu__container">
                  <div className="header__submenu__arrow"></div>
                  {data.links.map(
                    (links, i) =>
                      !links.block.document.type && (
                        <Fragment key={i}>
                          {links.block.document.data.description.text !== '' ? (
                            <section data-index={i}>
                              <div className="f--tablet">
                                <div>
                                  <p className="h2 c-text-black-lighter mb-3 mb-md-1 mt-3 mt-menu-0">
                                    {links.block.document.data.description.text}
                                  </p>
                                  <ul className="f">
                                    {links.block.document.data.links.map((link, index) => (
                                      <li key={index} className="header__card">
                                        <a
                                          href={link.block.document.data.link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <div className="f f-align-items-center">
                                            <div
                                              className="header__card__icon"
                                              style={{ background: link.block.document.data.icon_color }}
                                            >
                                              {link.block.document.data.icon.url && (
                                                <img
                                                  loading="lazy"
                                                  src={link.block.document.data.icon.url}
                                                  alt={link.block.document.data.title.text}
                                                />
                                              )}
                                            </div>
                                            <div className="header__card__content f f-direction-column">
                                              <span className="c-text-black-light">
                                                {link.block.document.data.title.text}
                                              </span>
                                              {link.block.document.data.subtitle && (
                                                <p className="c-text-black-lightest ft-tertiary mb-0">
                                                  {link.block.document.data.subtitle.text}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          {i === 0 && <div className="header__card__index">0{index + 1}</div>}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                {links.block.document.data.links_tutorials.length > 0 && (
                                  <div className="header__tutorials">
                                    <p className="h2 c-text-black-lighter mb-3 mb-md-1">{COMMON.FEATURES}</p>
                                    <ul className="f">
                                      {links.block.document.data.links_tutorials.map((link, index) => (
                                        <li key={index} className="header__card">
                                          <a
                                            href={link.block.document.data.link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <div className="f f-align-items-center">
                                              <div className="header__card__icon">
                                                {link.block.document.data.icon.url && (
                                                  <img
                                                    loading="lazy"
                                                    src={link.block.document.data.icon.url}
                                                    alt={link.block.document.data.title.text}
                                                  />
                                                )}
                                              </div>
                                              <span className="ft-tertiary c-text-black-light ft-600">
                                                {link.block.document.data.title.text}
                                              </span>
                                            </div>
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                    <a
                                      className="ft-tertiary link-arrow mt-7"
                                      href="https://hub.qovery.com/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {COMMON.SHOW_DOCUMENTATION}
                                      {iconRightSolid}
                                    </a>
                                  </div>
                                )}
                              </div>
                              <div
                                role="button"
                                tabIndex="0"
                                className="header__previous"
                                onClick={() => onClickPreviousBtn()}
                                onKeyDown={() => onClickPreviousBtn()}
                              >
                                {iconRightSolid}
                              </div>
                            </section>
                          ) : (
                            <section data-index={i}>
                              <ul className="f f-direction-column header__submenu__links mt-3 mt-menu-0">
                                {links.block.document.data.links.map((link, index) => (
                                  <li className="ft-500" key={index}>
                                    <NavLink data={link} />
                                  </li>
                                ))}
                              </ul>
                              <div
                                role="button"
                                tabIndex="0"
                                className="header__previous"
                                onClick={() => onClickPreviousBtn()}
                                onKeyDown={() => onClickPreviousBtn()}
                              >
                                {iconRightSolid}
                              </div>
                            </section>
                          )}
                        </Fragment>
                      )
                  )}
                </div>
              </div>
            </nav>
            <div className="header__btns">
              <Button className="btn btn--transparent mr-3" url="https://www.qovery.com/request-access?formId=yFDkReLr">
                {COMMON.BOOK_A_DEMO}
              </Button>
              <Button className="btn btn--white" url="https://start.qovery.com/">
                {COMMON.SIGN_UP}
              </Button>
            </div>
          </div>
          <button
            className="header__mobile"
            onClick={() => toggleMobileMenu(isOpen)}
            onKeyDown={() => toggleMobileMenu(isOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>
    </DetectClickOutside>
  )
}

export default Header
